import * as React from 'react';

import {
  Table,
  TableTopBar,
  BasicSingleCell,
  type GenericHeaderItems,
  type GenericObject,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {
  BodyMediumBold,
  BodySmall,
  ButtonTextSmall,
  TEXT_COLORS,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  ButtonDropdown,
  SimpleButtonDropdown,
} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {borderRadiusNone} from '@spaced-out/ui-design-system/lib/styles/variables/_border';
import {classify} from 'src/utils/classify';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useAgencyData} from 'src/hooks/useAgencyData';
import {useTableManager} from 'src/hooks/useTableManager';
import {formatToCustomDateString} from 'src/utils/analytics-genesis';
import FilterDropDown from 'src/components/analytics-genesis/journeys-dashboard/common/filter-drop-down';
import AllTableFooter from 'src/components/analytics-genesis/journeys-dashboard/common/all-table-footer';
import {DEFAULT_PAGE_SIZE} from 'src/components/analytics-genesis/byod/constants';

import css from './byod-table.css';


export type TableColumn = {key: string; label: string};

export type FetchDataParams = {
  offset: number;
  limit: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
};

export type ByodTableProps = {
  /** Widget display name shown in the table header. */
  label?: string;
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  totalCount: number;
  /** Currently selected columns (from widget_config_json). null = show all. */
  selectedColumns: string[] | null;
  onSelectedColumnsChange?: (columns: string[]) => void;
  isSavingColumns?: boolean;

  /**
   * Initial sort (restored from persisted widget_config_json).
   * ByodTable owns sort state after mount.
   */
  initialSort?: {sortBy: string; sortOrder: 'asc' | 'desc' | 'original'};

  /**
   * Single callback — fires on any state change the parent must re-fetch for
   * (page change, sort change). Omit to disable pagination/sort entirely
   * (e.g. static preview).
   */
  onFetchData?: (params: FetchDataParams) => void;

  pageSize?: number;
  isLoading?: boolean;

  /** Active filter count — shown as badge in header. */
  activeFilterCount?: number;

  /**
   * Increment to reset pagination to page 1 (e.g. when external filters change).
   * The table resets its internal page number whenever this value changes.
   */
  resetKey?: number;

  /** Three-dot menu actions. */
  onEdit?: () => void;
  onRemove?: () => void;
  isRemoving?: boolean;
};

export function ByodTable({
  label,
  columns,
  rows,
  totalCount,
  selectedColumns,
  onSelectedColumnsChange,
  isSavingColumns = false,
  initialSort,
  onFetchData,
  pageSize = DEFAULT_PAGE_SIZE,
  isLoading = false,
  activeFilterCount = 0,
  resetKey = 0,
  onEdit,
  onRemove,
  isRemoving = false,
}: ByodTableProps) {
  const {t, language, dynamicTranslate} = useLabelTranslation();
  const agency = useAgencyData();
  const agencyTimezone = agency?.timezone;

  const {
    tableState,
    handlePageChange: tmPageChange,
    handleSortChange: tmSortChange,
  } = useTableManager({
    sort: initialSort ?? {sortBy: '', sortOrder: 'original'},
    pageNumber: 1,
  });

  // Reset to page 1 when external state changes (e.g. filters applied).
  const resetKeyRef = React.useRef(resetKey);
  React.useEffect(() => {
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      tmPageChange(1);
    }
  }, [resetKey, tmPageChange]);

  // Ref prevents effect from firing when parent rebuilds the callback.
  const onFetchDataRef = React.useRef(onFetchData);
  onFetchDataRef.current = onFetchData;

  // Skip initial mount — parent handles the first load.
  const didMountRef = React.useRef(false);
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const fetch = onFetchDataRef.current;
    if (!fetch) {
      return;
    }
    const offset = (tableState.pageNumber - 1) * pageSize;
    const params: FetchDataParams = {offset, limit: pageSize};
    if (tableState.sort.sortBy && tableState.sort.sortOrder !== 'original') {
      params.sort_by = tableState.sort.sortBy;
      params.sort_direction = tableState.sort.sortOrder;
    }
    fetch(params);
  }, [
    tableState.pageNumber,
    tableState.sort.sortBy,
    tableState.sort.sortOrder,
    pageSize,
  ]);

  const handlePageChange = React.useCallback(
    (page?: number | null) => {
      tmPageChange(page);
    },
    [tmPageChange],
  );

  const handleSort = React.useCallback(
    (sortKey: string, sortDirection: string) => {
      const sortOrder = (sortDirection as 'asc' | 'desc') || 'asc';
      tmSortChange(sortKey, sortOrder);
    },
    [tmSortChange],
  );

  const visibleColumns = React.useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return columns;
    }
    const selectedSet = new Set(selectedColumns);
    return columns.filter((col) => selectedSet.has(col.key));
  }, [columns, selectedColumns]);

  const columnMenuOptions = React.useMemo(
    () =>
      columns.map((col) => ({
        key: col.key,
        label: dynamicTranslate(col.label),
      })),
    [columns, dynamicTranslate],
  );

  const selectedFilterKeys = React.useMemo(
    () =>
      selectedColumns && selectedColumns.length > 0
        ? selectedColumns
        : columns.map((c) => c.key),
    [selectedColumns, columns],
  );

  const handleColumnSave = React.useCallback(
    async (keys: string[]) => {
      onSelectedColumnsChange?.(keys);
    },
    [onSelectedColumnsChange],
  );

  const headers = React.useMemo(
    () =>
      visibleColumns.map((col) => ({
        key: col.key,
        label: dynamicTranslate(col.label),
        sortable: Boolean(onFetchData),
        className: css.tableHeaderColumn,
        render: ({
          data,
          className,
        }: {
          data: Record<string, unknown>;
          className?: string;
        }) => {
          const cellValue = data[col.key];
          const arrayItems = parseAsArray(cellValue);
          if (arrayItems) {
            return (
              <ArrayCell
                items={arrayItems}
                className={classify(className, css.tableHeaderColumn)}
              />
            );
          }
          return (
            <BasicSingleCell
              className={classify(className, css.tableHeaderColumn)}
            >
              <span className={css.cellText}>
                <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 20}}>
                  {formatCellValue(cellValue, t, language, agencyTimezone)}
                </TruncatedTextWithTooltip>
              </span>
            </BasicSingleCell>
          );
        },
      })),
    [
      visibleColumns,
      onFetchData,
      t,
      language,
      agencyTimezone,
      dynamicTranslate,
    ],
  );

  const entries = React.useMemo(
    () =>
      rows.map((row, i) => ({
        ...row,
        _byod_row_id: `${tableState.pageNumber}-${i}`,
      })),
    [rows, tableState.pageNumber],
  );

  const hasData = rows.length > 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const menuOptions = React.useMemo(() => {
    const opts = [];
    if (onEdit) {
      opts.push({
        key: 'edit',
        label: t('Edit_widget', 'Edit widget'),
        iconLeft: 'pencil',
      });
    }
    if (onRemove) {
      opts.push({
        key: 'remove',
        label: t('Remove_widget', 'Remove widget'),
        iconLeft: 'trash',
      });
    }
    return opts;
  }, [onEdit, onRemove, t, language]);

  const handleMenuSelect = React.useCallback(
    (opt: {key: string}) => {
      if (opt.key === 'edit') {
        onEdit?.();
      } else if (opt.key === 'remove') {
        void onRemove?.();
      }
    },
    [onEdit, onRemove],
  );

  return (
    <div
      className={css.byodTableContainer}
      style={
        {'--number-of-columns': visibleColumns.length} as React.CSSProperties
      }
    >
      <TableTopBar>
        <div className={css.headerLeft}>
          {label && <BodyMediumBold>{label}</BodyMediumBold>}
          {activeFilterCount > 0 && (
            <BodySmall color="secondary">
              {t('count_filters_applied', {
                count: activeFilterCount,
                defaultValue: '{{count}} filters applied',
              })}
            </BodySmall>
          )}
        </div>
        <div className={css.headerRight}>
          {onSelectedColumnsChange && columns.length > 0 && (
            <FilterDropDown
              headerTitle={t('Edit_table_columns', 'Edit table columns')}
              menuOptions={columnMenuOptions}
              selectedFilterKeys={selectedFilterKeys}
              handleOnSave={handleColumnSave}
              isSavingTableFilters={isSavingColumns}
              // eslint-disable-next-line no-empty-function
              onSaveCallback={() => {}}
            />
          )}
          {menuOptions.length > 0 && !isRemoving && (
            <SimpleButtonDropdown
              type="ghost"
              size="small"
              iconLeftName="ellipsis-v"
              ariaLabel={t('Widget_actions', 'Widget actions')}
              options={menuOptions}
              onOptionSelect={handleMenuSelect}
              isFluid={false}
            />
          )}
        </div>
      </TableTopBar>

      <Table
        key="byod-table"
        borderRadius={borderRadiusNone}
        classNames={{
          wrapper: css.tableWrapper,
        }}
        entries={entries}
        headers={
          headers as GenericHeaderItems<Record<string, unknown>, GenericObject>
        }
        idName="_byod_row_id"
        isLoading={isLoading}
        showHeader={hasData || isLoading}
        enableInternalSorting={!onFetchData}
        onSort={onFetchData ? handleSort : undefined}
        emptyText={
          <EmptyState
            title={t('No_result_found', 'No result found')}
            description={t(
              'No_data_available_for_the_selected_filters',
              'No data available for the selected filters.',
            )}
            imageVariant="data"
          />
        }
        stickyHeader
      />

      <AllTableFooter
        pageNumber={tableState.pageNumber}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        total={totalCount}
        pageSize={pageSize}
      />
    </div>
  );
}

// Matches ISO 8601 date strings with or without timezone:
// 2026-01-15T10:30:00Z, 2026-01-15T10:30:00.534047, 2026-01-15T10:30:00+05:30
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;

// Tries to extract an array from a cell value.
// Handles: native arrays, JSON-encoded array strings. Returns null if not an array.
function parseAsArray(value: unknown): MenuOption[] | null {
  let arr: unknown[] | null = null;
  if (Array.isArray(value)) {
    arr = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          arr = parsed;
        }
      } catch {
        // not valid JSON
      }
    }
  }
  if (!arr || arr.length === 0) {
    return null;
  }
  return arr.map((item, i) => {
    if (item == null) {return {key: String(i), label: ''};}
    if (typeof item === 'object' && !Array.isArray(item)) {
      const obj = item as Record<string, unknown>;
      const nameKey = Object.keys(obj).find((k) => /name|label|title/i.test(k));
      const label =
        nameKey != null && obj[nameKey] != null
          ? String(obj[nameKey])
          : String(
              Object.values(obj).find((v) => typeof v === 'string') ??
                JSON.stringify(item),
            );
      // Use an id-like key if present, otherwise index
      const idKey = Object.keys(obj).find((k) => /id$/i.test(k));
      const key =
        idKey != null && obj[idKey] != null ? String(obj[idKey]) : String(i);
      return {key, label};
    }
    return {key: String(i), label: String(item)};
  });
}

// Generic dropdown cell for array values.
// 0 items → N/A, 1 item → label text, N items → "N items" dropdown.
function ArrayCell({
  items,
  className,
}: {
  items: MenuOption[];
  className?: string;
}) {
  const {t} = useLabelTranslation();

  if (items.length === 0) {
    return (
      <BasicSingleCell className={className}>
        <span className={css.cellText}>
          <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 20}}>
            {t('NA', 'N/A')}
          </TruncatedTextWithTooltip>
        </span>
      </BasicSingleCell>
    );
  }

  if (items.length === 1) {
    return (
      <BasicSingleCell className={className}>
        <span className={css.cellText}>
          <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
            {items[0].label || t('NA', 'N/A')}
          </TruncatedTextWithTooltip>
        </span>
      </BasicSingleCell>
    );
  }

  return (
    <BasicSingleCell className={className}>
      <ButtonDropdown
        menu={{
          options: items,
          size: 'medium',
          resolveLabel: (option: MenuOption) => (
            <ButtonTextSmall color={TEXT_COLORS.secondary}>
              <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
                {option.label}
              </TruncatedTextWithTooltip>
            </ButtonTextSmall>
          ),
        }}
        type="ghost"
      >
        <ButtonTextSmall>
          <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
            {t('count_items', {
              count: items.length,
              defaultValue: '{{count}} items',
            })}
          </TruncatedTextWithTooltip>
        </ButtonTextSmall>
      </ButtonDropdown>
    </BasicSingleCell>
  );
}

function formatCellValue(
  value: unknown,
  t: (key: string, fallback: string) => string,
  language?: string,
  agencyTimezone?: string,
): string {
  if (value == null) {
    return t('NA', 'N/A');
  }
  if (typeof value === 'number') {
    return value.toLocaleString(language);
  }
  if (typeof value === 'boolean') {
    return value ? t('Yes', 'Yes') : t('No', 'No');
  }
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    return formatToCustomDateString(
      value,
      agencyTimezone,
      'ddd, M/D/YY, h:mm A',
      language,
    );
  }
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'object') {
    // Python Decimal serialization: { __reduce__: [{ "py/type": "decimal.Decimal" }, ["30.00"]] }
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.__reduce__) && Array.isArray(obj.__reduce__[1])) {
      const numStr = obj.__reduce__[1][0];
      if (typeof numStr === 'string') {
        const n = Number(numStr);
        return Number.isFinite(n) ? n.toLocaleString(language) : numStr;
      }
    }
    return JSON.stringify(value);
  }
  return String(value);
}
